import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import {
  FiAward,
  FiTrendingUp,
  FiClock,
  FiCheckCircle,
  FiXCircle,
  FiRefreshCw,
  FiPlus,
  FiGift,
  FiActivity,
  FiX,
  FiCheck,
} from "react-icons/fi";
import {
  getRewardPointsCount,
  getRewardLogs,
  getRewardActions,
  getRedeemHistory,
  redeemRewardPoints,
} from "../../../api/customer/customerFeatureApis";

const CustomerRewardsHub = () => {
  const authState = useSelector((state) => state.auth);
  const loggedInUser = authState?.user ?? authState;
  const token =
    loggedInUser?.refreshToken ||
    authState?.refreshToken ||
    loggedInUser?.token ||
    authState?.token;

  const [activeTab, setActiveTab] = useState("actions"); // 'actions' | 'logs' | 'redemptions'

  const [totalPoints, setTotalPoints] = useState(0);
  const [logs, setLogs] = useState([]);
  const [actions, setActions] = useState([]);
  const [redemptions, setRedemptions] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Redeem modal state
  const [isRedeemOpen, setIsRedeemOpen] = useState(false);
  const [redeemPointsAmount, setRedeemPointsAmount] = useState("10");
  const [submittingRedeem, setSubmittingRedeem] = useState(false);
  const [redeemError, setRedeemError] = useState(null);
  const [redeemSuccess, setRedeemSuccess] = useState(null);

  const fetchAllRewardsData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);

    try {
      const [countRes, logRes, actionRes, redeemRes] = await Promise.allSettled([
        getRewardPointsCount(token),
        getRewardLogs(token),
        getRewardActions(token),
        getRedeemHistory(token),
      ]);

      if (countRes.status === "fulfilled" && countRes.value?.statusType === "SUCCESS") {
        setTotalPoints(countRes.value.rewardPoints || 0);
      }

      if (logRes.status === "fulfilled" && logRes.value?.statusType === "SUCCESS") {
        setLogs(logRes.value.userPointLogs || []);
      }

      if (actionRes.status === "fulfilled" && actionRes.value?.statusType === "SUCCESS") {
        setActions(actionRes.value.rewardActions || []);
      }

      if (redeemRes.status === "fulfilled" && redeemRes.value?.statusType === "SUCCESS") {
        setRedemptions(redeemRes.value.redeemRequests || []);
      }
    } catch (err) {
      setError("Unable to load rewards data. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchAllRewardsData();
  }, [fetchAllRewardsData]);

  const handleRedeemSubmit = async (e) => {
    e.preventDefault();
    const amount = Number(redeemPointsAmount);

    if (isNaN(amount) || amount <= 0) {
      setRedeemError("Please enter a valid points amount greater than zero.");
      return;
    }

    if (amount > totalPoints) {
      setRedeemError(`You cannot redeem more than your available balance (${totalPoints} points).`);
      return;
    }

    setSubmittingRedeem(true);
    setRedeemError(null);

    try {
      const res = await redeemRewardPoints(token, amount);
      if (res?.statusType === "SUCCESS" || res?.text) {
        setRedeemSuccess(res?.text || "Points redemption request submitted successfully!");
        setRedeemPointsAmount("");
        setTimeout(() => {
          setIsRedeemOpen(false);
          setRedeemSuccess(null);
          fetchAllRewardsData();
        }, 1800);
      } else {
        setRedeemError(res?.text || "Failed to submit redemption request.");
      }
    } catch (err) {
      setRedeemError(err?.response?.data?.text || "An error occurred while submitting redemption.");
    } finally {
      setSubmittingRedeem(false);
    }
  };

  const getStatusBadge = (status) => {
    const s = (status || "").toUpperCase();
    if (s === "APPROVED" || s === "COMPLETED") {
      return <span className="badge bg-success-subtle text-success px-2 py-1">Approved</span>;
    }
    if (s === "REJECTED") {
      return <span className="badge bg-danger-subtle text-danger px-2 py-1">Rejected</span>;
    }
    return <span className="badge bg-warning-subtle text-warning px-2 py-1">Pending Review</span>;
  };

  return (
    <div className="d-flex flex-column gap-4 w-100">
      {/* 1. Header Card with Total Points */}
      <div
        className="rounded-4 p-4 p-md-5 text-white shadow-sm d-flex flex-wrap align-items-center justify-content-between gap-4"
        style={{
          background: "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)",
          border: "1px solid #334155",
        }}
      >
        <div className="d-flex align-items-center gap-3">
          <div
            className="d-flex align-items-center justify-content-center rounded-3 text-warning"
            style={{ width: "64px", height: "64px", background: "rgba(251, 191, 36, 0.15)" }}
          >
            <FiAward size={36} />
          </div>
          <div>
            <div className="text-light opacity-75 small text-uppercase fw-bold tracking-wider">
              Available Reward Balance
            </div>
            <div className="display-6 fw-bold text-white mb-0">
              {totalPoints.toLocaleString("en-IN")}{" "}
              <span className="fs-5 text-warning fw-semibold">Points</span>
            </div>
          </div>
        </div>

        <div className="d-flex align-items-center gap-2">
          <button
            type="button"
            className="btn btn-warning px-4 py-2 fw-semibold d-inline-flex align-items-center gap-2 shadow-sm"
            onClick={() => {
              setRedeemError(null);
              setRedeemSuccess(null);
              setIsRedeemOpen(true);
            }}
            disabled={totalPoints <= 0}
          >
            <FiGift size={18} />
            <span>Redeem Points</span>
          </button>

          <button
            type="button"
            className="btn btn-outline-light px-3 py-2 d-inline-flex align-items-center gap-1"
            onClick={fetchAllRewardsData}
            disabled={loading}
            title="Refresh rewards"
          >
            <FiRefreshCw size={16} className={loading ? "spin" : ""} />
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger d-flex align-items-center justify-content-between p-3" role="alert">
          <span>{error}</span>
          <button type="button" className="btn btn-sm btn-outline-danger" onClick={fetchAllRewardsData}>
            Retry
          </button>
        </div>
      )}

      {/* 2. Tabs */}
      <div className="border-bottom border-slate-200">
        <ul className="nav nav-tabs border-0 gap-2" role="tablist">
          <li className="nav-item" role="presentation">
            <button
              className={`nav-link border-0 fw-semibold px-4 py-2 ${
                activeTab === "actions" ? "active text-primary border-bottom border-primary border-2" : "text-secondary"
              }`}
              onClick={() => setActiveTab("actions")}
              type="button"
            >
              <span className="d-flex align-items-center gap-2">
                <FiTrendingUp size={16} />
                <span>Earnable Actions ({actions.length})</span>
              </span>
            </button>
          </li>
          <li className="nav-item" role="presentation">
            <button
              className={`nav-link border-0 fw-semibold px-4 py-2 ${
                activeTab === "logs" ? "active text-primary border-bottom border-primary border-2" : "text-secondary"
              }`}
              onClick={() => setActiveTab("logs")}
              type="button"
            >
              <span className="d-flex align-items-center gap-2">
                <FiActivity size={16} />
                <span>Points Log ({logs.length})</span>
              </span>
            </button>
          </li>
          <li className="nav-item" role="presentation">
            <button
              className={`nav-link border-0 fw-semibold px-4 py-2 ${
                activeTab === "redemptions" ? "active text-primary border-bottom border-primary border-2" : "text-secondary"
              }`}
              onClick={() => setActiveTab("redemptions")}
              type="button"
            >
              <span className="d-flex align-items-center gap-2">
                <FiClock size={16} />
                <span>Redemptions History ({redemptions.length})</span>
              </span>
            </button>
          </li>
        </ul>
      </div>

      {/* 3. Tab Contents */}
      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status"></div>
          <p className="text-muted small mt-2">Loading reward program records...</p>
        </div>
      ) : activeTab === "actions" ? (
        <div>
          <p className="text-secondary small mb-3">
            Earn rewards points effortlessly by completing standard real estate and community engagement actions.
          </p>

          {actions.length === 0 ? (
            <div className="text-center py-5 bg-white rounded-3 border border-slate-200">
              <p className="text-muted mb-0">No active promotional reward actions found at the moment.</p>
            </div>
          ) : (
            <div className="row g-3">
              {actions.map((act) => (
                <div key={act.id} className="col-12 col-md-6 col-lg-4">
                  <div className="p-4 bg-white rounded-3 border border-slate-200 shadow-sm h-100 d-flex flex-column justify-content-between">
                    <div>
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <span className="badge bg-primary-subtle text-primary fw-semibold px-2 py-1">
                          +{act.points || act.point || 10} Points
                        </span>
                        <span className="text-muted small">Action</span>
                      </div>
                      <h3 className="fs-6 fw-bold text-dark mb-1">
                        {act.action_name || act.title || act.name || "Platform Milestone"}
                      </h3>
                      <p className="text-secondary small mb-3">
                        {act.description || "Earn rewards by performing this platform activity."}
                      </p>
                    </div>
                    <div className="pt-2 border-top border-slate-100 d-flex align-items-center justify-content-between text-muted small">
                      <span>Status: Verified</span>
                      <span className="text-success fw-semibold">Available</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : activeTab === "logs" ? (
        <div>
          {logs.length === 0 ? (
            <div className="text-center py-5 bg-white rounded-3 border border-slate-200">
              <p className="text-muted mb-0">No reward points activity logged yet. Start engaging to build your score!</p>
            </div>
          ) : (
            <div className="bg-white rounded-3 border border-slate-200 shadow-sm overflow-hidden">
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th className="px-3 py-2 small fw-bold">Action / Trigger</th>
                      <th className="px-3 py-2 small fw-bold">Points</th>
                      <th className="px-3 py-2 small fw-bold">Date & Time</th>
                      <th className="px-3 py-2 small fw-bold">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((log) => {
                      const logDate = log.created_on
                        ? new Date(log.created_on).toLocaleString("en-IN", {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })
                        : "Recent";

                      return (
                        <tr key={log.id}>
                          <td className="px-3 py-3">
                            <div className="fw-semibold text-dark">
                              {log.action_type || log.action_name || "Platform Reward"}
                            </div>
                            {log.note && <div className="text-muted small">{log.note}</div>}
                          </td>
                          <td className="px-3 py-3">
                            <span className="badge bg-success-subtle text-success fw-bold px-2 py-1">
                              +{log.points || log.point || 0} Points
                            </span>
                          </td>
                          <td className="px-3 py-3 text-secondary small">{logDate}</td>
                          <td className="px-3 py-3">
                            <span className="badge bg-light text-secondary border">Credited</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div>
          {redemptions.length === 0 ? (
            <div className="text-center py-5 bg-white rounded-3 border border-slate-200">
              <p className="text-muted mb-0">No redemption requests submitted yet.</p>
            </div>
          ) : (
            <div className="bg-white rounded-3 border border-slate-200 shadow-sm overflow-hidden">
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th className="px-3 py-2 small fw-bold">Request ID</th>
                      <th className="px-3 py-2 small fw-bold">Points Redeemed</th>
                      <th className="px-3 py-2 small fw-bold">Requested Date</th>
                      <th className="px-3 py-2 small fw-bold">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {redemptions.map((req) => {
                      const reqDate = req.created_on
                        ? new Date(req.created_on).toLocaleString("en-IN", {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                          })
                        : "Recent";

                      return (
                        <tr key={req.id}>
                          <td className="px-3 py-3 fw-semibold text-dark">#{req.id}</td>
                          <td className="px-3 py-3">
                            <span className="badge bg-primary-subtle text-primary fw-bold px-2 py-1">
                              {req.points} Points
                            </span>
                          </td>
                          <td className="px-3 py-3 text-secondary small">{reqDate}</td>
                          <td className="px-3 py-3">{getStatusBadge(req.status)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Redeem Modal */}
      {isRedeemOpen && (
        <div className="customer-modal-overlay" role="dialog" aria-modal="true">
          <div className="customer-modal" style={{ maxWidth: "460px" }}>
            <div className="customer-modal__header">
              <h2 className="customer-modal__title d-flex align-items-center gap-2">
                <FiGift className="text-warning" />
                <span>Redeem Reward Points</span>
              </h2>
              <button
                type="button"
                className="btn btn-sm btn-light border-0"
                onClick={() => setIsRedeemOpen(false)}
                aria-label="Close dialog"
              >
                <FiX size={20} />
              </button>
            </div>

            <form onSubmit={handleRedeemSubmit}>
              <div className="customer-modal__body">
                {redeemError && (
                  <div className="alert alert-danger py-2 small mb-0">{redeemError}</div>
                )}
                {redeemSuccess && (
                  <div className="alert alert-success py-2 small mb-0 d-flex align-items-center gap-2">
                    <FiCheck />
                    <span>{redeemSuccess}</span>
                  </div>
                )}

                <div className="p-3 bg-light rounded-3 text-center">
                  <div className="text-muted small">Available Balance</div>
                  <div className="fs-3 fw-bold text-dark">{totalPoints} Points</div>
                </div>

                <div className="customer-form-group">
                  <label htmlFor="redeem-amount" className="customer-form-label">
                    Points to Redeem <span className="required">*</span>
                  </label>
                  <input
                    id="redeem-amount"
                    type="number"
                    min="1"
                    max={totalPoints}
                    className="customer-form-input"
                    placeholder="Enter points count (e.g. 100)"
                    value={redeemPointsAmount}
                    onChange={(e) => setRedeemPointsAmount(e.target.value)}
                    disabled={submittingRedeem}
                  />
                  <span className="text-muted small">
                    Redemption requests are reviewed and credited directly to your registered bank account or wallet.
                  </span>
                </div>
              </div>

              <div className="customer-modal__footer">
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => setIsRedeemOpen(false)}
                  disabled={submittingRedeem}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-warning btn-sm px-4 fw-semibold"
                  disabled={submittingRedeem || !redeemPointsAmount}
                >
                  {submittingRedeem ? "Submitting..." : "Submit Redemption Request"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerRewardsHub;
